import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getStoreProductsApi,
  getStoreBrandsApi,
  editStoreProductUpSellApi,
  removeStoreProductRelatedLinksAPI
} from 'api';
import {addUpSellLinks} from 'redux/actions/products';
import {
  Button,
  CircularProgress,
  Drawer,
  Tooltip,
  TableContainer,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Paper,
  Icon,
  IconButton,
  Chip
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import logo from 'components/Logo/logo-dermpro.svg';
import { imgBaseUrl } from 'configs';
import { BackdropCircular } from 'components';


const UpSell = ({product}) => {
  const dispatch = useDispatch();
  const [openUpSellDrawer, setOpenUpSellDrawer] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [upsellProductsToAdd, setUpsellProductsToAdd] = useState([]);

  const {
    store_products: {catalogProducts},
    productsLoading,
    savingProductUpsellLinks,
    removeProductRelatedLinks
  } = useSelector((state) => state.products);
  const { storeBrands } = useSelector((state) => state.brands);
  const {product_links} = product;
  const { stores = [] } = useSelector((state) => state.user.profile);
  const userStore = useSelector((state) => state.user?.profile?.stores?.[0]);
  const upSellData = product_links.filter((item) => item.link_type === 'upsell');
  const upsellSkus = upSellData.map((item) => item.linked_product_sku);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  useEffect(() => {
    if (!catalogProducts && userStore?.isCreated) {
      getStoreProductsApi({dispatch});
    }

    if (storeBrands.length === 0) {
      getStoreBrandsApi({dispatch});
    }
  }, []);

  useEffect(() => {
    if (catalogProducts?.length && storeBrands?.length) {
      const filteredCatalog = catalogProducts.filter((p) => {
        return upsellSkus.length === 0 ?
          product.sku !== p.sku
          :
          product.sku !== p.sku &&
          !upsellSkus.includes(p.sku);
      });

      const newCatalog = filteredCatalog.map((product) => {
        const productImagePath = product?.custom_attributes?.find((attr) => attr.attribute_code === 'image')?.value;
        const productBrand = product?.custom_attributes?.find((attr) => attr.attribute_code === 'brand')?.value;
        return {
          ...product,
          image: productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo,
          price: +Number.parseFloat(product?.price).toFixed(2) || '-',
          brand: storeBrands?.find((b) => b.option_id === productBrand)?.value || '',
          status: product?.status === 1 ? 'Enabled' : 'Disabled'
        };
      });

      const allCatalogProducts = catalogProducts.map((product) => {
        const productImagePath = product?.custom_attributes?.find((attr) => attr.attribute_code === 'image')?.value;
        const productBrand = product?.custom_attributes?.find((attr) => attr.attribute_code === 'brand')?.value;
        return {
          ...product,
          image: productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo,
          price: +Number.parseFloat(product?.price).toFixed(2) || '-',
          brand: storeBrands?.find((b) => b.option_id === productBrand)?.value || ''
        };
      });
      setUpsellProductsToAdd(newCatalog);
      setCatalog(allCatalogProducts);
    }
  }, [catalogProducts, storeBrands, product_links]);

  const handleSaveUpSellLinks = async (selectedRows) => {
    const selectedIndexes = selectedRows.data.map((i) => i.dataIndex);
    const selectedSkus = selectedIndexes.map((idx) => upsellProductsToAdd[idx].sku);

    const filtered = upsellProductsToAdd.filter((product) => !selectedSkus.includes(product.sku));
    setUpsellProductsToAdd(filtered);

    const selectedUpsellLinks = selectedSkus.map((sku) => {
      const data = {
        sku: product.sku,
        link_type: 'upsell',
        linked_product_sku: sku,
        linked_product_type: 'simple'
      };

      dispatch(addUpSellLinks(data));
      return data;
    });

    const productToUpdate = {
      id: product.id,
      sku: product.sku,
      product_links: [...product.product_links, ...selectedUpsellLinks]
    };

    await editStoreProductUpSellApi({
      dispatch,
      product: productToUpdate
    });
    setOpenUpSellDrawer(savingProductUpsellLinks);
  };

  const removeProductsFromUpsellItems = (linkedProductSku) => {
    const removedProduct = catalog.find((value) => value.sku === linkedProductSku);
    setUpsellProductsToAdd((products) => [...products, removedProduct]);

    const filteredProductLinks = product.product_links.filter((item) => {
      return (
        (item.linked_product_sku !== linkedProductSku && item.link_type === 'upsell')
        || item.link_type === 'crosssell'
      );
    });

    const productToUpdate = {
      id: product.id,
      sku: product.sku,
      product_links: filteredProductLinks
    };

    removeStoreProductRelatedLinksAPI({
      dispatch,
      product: productToUpdate
    });
  };

  const BackButton = () => (
    <IconButton
      style={{marginLeft: '-16px'}}
      title="Close"
      onClick={() => setOpenUpSellDrawer(false)}
    >
      <Icon>close</Icon>
    </IconButton>
  );

  const columns = [
    {
      name: 'sku',
      label: 'Item Name',
      options: {
        sort: false,
        filter: false,
        display: false
      },
    },
    {
      name: 'name',
      label: 'Item Name',
      options: {
        sort: true,
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          const product = upsellProductsToAdd[dataIndex];
          return (
            <div className="flex items-center">
              <img
                src={loaded ? product.image : logo}
                alt={product.name}
                className="max-w-60 max-h-60 w-full"
                onLoad={() => setLoaded(true)}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = logo;
                }}
              />
              <div className="ml-3">
                <p className="my-0 text-15" >{product.name}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'brand',
      label: 'Brand',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const product = upsellProductsToAdd[dataIndex];
          return (
            <div className="flex items-center">
              <div className="ml-3">
                <p className="my-0 text-15">{product.brand}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const product = upsellProductsToAdd[dataIndex];
          return (
            <div className="flex items-center">{`$${Number(product?.price) ? product.price.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,') : product?.price}`}</div>
          );
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        filterType: 'dropdown',
        customBodyRenderLite: (dataIndex) => {
          const product = upsellProductsToAdd[dataIndex];
          return (
            <Chip label={product.status} color={product.status === 'Enabled' ? 'primary' : 'grey'} />
          );
        },
      },
    },
  ];

  return (
    <div className="m-4">
      {!upSellData.length && <div className="text-muted">You don&apos;t have linked Upsell items.</div>}
      {!!upSellData.length && !!catalog.length &&
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {upSellData.map((item) => {
              const {linked_product_sku} = item;
              const product = catalog.find((product) => product.sku === linked_product_sku);

              if (!product) {
                return (
                  <TableRow key={linked_product_sku}>
                    <TableCell colSpan={4}>
                      Can&apos;t find product linked product with such SKU ({linked_product_sku}). Please contact our support team to fix this issue.
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow key={linked_product_sku}>
                  <TableCell>
                    <div className="flex items-center">
                      <div className="w-full max-w-60 text-center">
                        <img
                          src={product.image}
                          alt={product.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = logo;
                          }}
                          className="max-h-60 max-w-60"
                        />
                      </div>
                      <div className="px-2">{product.name}</div>
                    </div>
                  </TableCell>
                  <TableCell>{product.brand}</TableCell>
                  <TableCell>${Number(product.price) ? product.price.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') : product.price}</TableCell>
                  <TableCell>
                    {
                      <Chip
                        label={product.status == 1 ? 'Enabled' : 'Disabled'}
                        color={product.status === 1 ? 'primary' : 'grey'}
                      />
                    }
                  </TableCell>
                  <TableCell>
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={() => removeProductsFromUpsellItems(linked_product_sku)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      }
      <Button
        variant="contained"
        className="bg-light-primary my-4"
        onClick={() => setOpenUpSellDrawer(true)}
      >
        Add Upsell Items
      </Button>
      <Drawer
        open={openUpSellDrawer}
        onClose={() => setOpenUpSellDrawer(false)}
        anchor="right"
      >
        {productsLoading ?
          <div className="flex-column items-center justify-center m-20">
            <CircularProgress className="mb-10" />
            Catalog Items Data Is Loading...
          </div>
          :
          <>
            <MUIDataTable
              title={<BackButton />}
              data={upsellProductsToAdd}
              columns={columns}
              options={{
                filterType: 'textField',
                responsive: 'standard',
                selectableRows: 'multiple',
                fixedHeader: true,
                tableBodyHeight: '90vh',
                search: true,
                filter: true,
                download: false,
                print: false,
                pagination: true,
                viewColumns: false,
                rowsPerPageOptions: [10, 20, 40, 80, 100],
                customToolbarSelect: (selectedRows) => {
                  return (
                    <Tooltip title={'Publish Selected'} className="m-2">
                      <Button variant="contained" color="primary" onClick={() => handleSaveUpSellLinks(selectedRows)}>
                        Add Selected
                      </Button>
                    </Tooltip>
                  );
                }
              }}
              className="pl-10"
            />
            <BackdropCircular loading={savingProductUpsellLinks} />
          </>
        }
      </Drawer>
      <BackdropCircular loading={savingProductUpsellLinks || removeProductRelatedLinks} />
    </div>
  );
};

export default UpSell;
